import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { num, formatApiError } from "@/lib/api";
import { Card, PageHeader, Btn, Modal, Input, Select, StatusBadge, EmptyState, Th, Td } from "@/components/admin/ui";

export default function Tasks() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ items: [], total: 0 });
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [workers, setWorkers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", assigned_worker_id: "", campaign_id: "", priority: "medium", due_date: "", customer: "", instructions: "", required_evidence: "" });

  const load = useCallback(() => {
    api.get("/tasks?limit=50").then((r) => setData(r.data)).catch(() => {});
    api.get("/submissions").then((r) => setSubs(r.data.items)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/workers?limit=100").then((r) => setWorkers(r.data.items));
    api.get("/campaigns").then((r) => setCampaigns(r.data.items));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
      payload.title = form.title;
      await api.post("/tasks", payload);
      toast.success("Task created & assigned"); setOpen(false); load();
      setForm({ title: "", description: "", assigned_worker_id: "", campaign_id: "", priority: "medium", due_date: "", customer: "", instructions: "", required_evidence: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const review = async (taskId, decision) => {
    try { await api.post(`/tasks/${taskId}/review`, { decision }); toast.success(`Task ${decision}d`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Tasks" subtitle={`${num(data.total)} tasks · ${subs.length} awaiting review`}
        action={<Btn data-testid="create-task-btn" onClick={() => setOpen(true)}>+ Create Task</Btn>} />
      <div className="flex gap-2">
        <button onClick={() => setTab("all")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "all" ? "bg-[#1D4ED8] text-white" : "bg-white/5 text-slate-400"}`} data-testid="tasks-tab-all">All Tasks</button>
        <button onClick={() => setTab("review")} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === "review" ? "bg-[#1D4ED8] text-white" : "bg-white/5 text-slate-400"}`} data-testid="tasks-tab-review">Review Queue ({subs.length})</button>
      </div>

      {tab === "all" ? (
        <Card>
          {data.items.length === 0 ? <EmptyState title="No tasks yet" desc="Create a task and assign it to a worker to get started." action={<Btn onClick={() => setOpen(true)}>+ Create Task</Btn>} /> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-white/10"><tr><Th>Task ID</Th><Th>Title</Th><Th>Assigned</Th><Th>Priority</Th><Th>Status</Th><Th>Due</Th></tr></thead>
                <tbody>
                  {data.items.map((t) => (
                    <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`task-row-${t.task_id}`}>
                      <Td className="font-mono-metric text-[#1D4ED8]">{t.task_id}</Td>
                      <Td className="text-white font-medium">{t.title}</Td>
                      <Td className="text-slate-400">{t.assigned_worker_id || "—"}</Td>
                      <Td><StatusBadge status={t.priority} /></Td>
                      <Td><StatusBadge status={t.status} /></Td>
                      <Td className="text-slate-400 text-xs">{t.due_date ? t.due_date.slice(0, 10) : "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        <Card>
          {subs.length === 0 ? <EmptyState title="Review queue is empty" desc="Submitted tasks will appear here for approval." /> : (
            <div className="divide-y divide-white/5">
              {subs.map((s) => (
                <div key={s.id} className="p-4 flex items-start justify-between gap-4" data-testid={`submission-${s.task_id}`}>
                  <div>
                    <div className="font-medium text-white">{s.task_title} <span className="font-mono-metric text-[#1D4ED8] text-sm">{s.task_id}</span></div>
                    <div className="text-sm text-slate-400 mt-1">{s.notes || "No notes"} · Outcome: {s.outcome || "—"}</div>
                    <div className="text-xs text-slate-500 mt-1">By {s.worker_id} · {s.submitted_at?.slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Btn data-testid={`approve-${s.task_id}`} onClick={() => review(s.task_id, "approve")}>Approve</Btn>
                    <Btn variant="danger" data-testid={`reject-${s.task_id}`} onClick={() => review(s.task_id, "reject")}>Reject</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create Task">
        <form onSubmit={create} className="space-y-3">
          <Input label="Title" data-testid="task-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Assign to worker" data-testid="task-worker-select" value={form.assigned_worker_id} onChange={(e) => setForm({ ...form, assigned_worker_id: e.target.value })}>
              <option value="">— Unassigned</option>{workers.map((w) => <option key={w.id} value={w.worker_id}>{w.worker_id} · {w.name}</option>)}
            </Select>
            <Select label="Campaign" value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}>
              <option value="">—</option>{campaigns.map((c) => <option key={c.id} value={c.campaign_id}>{c.name}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </Select>
            <Input label="Due date" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <Input label="Customer / business" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} />
          <Input label="Required evidence" value={form.required_evidence} onChange={(e) => setForm({ ...form, required_evidence: e.target.value })} placeholder="Photo + notes" />
          <Btn data-testid="task-submit" className="w-full">Create Task</Btn>
        </form>
      </Modal>
    </div>
  );
}
